import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { CardProps } from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import {
  ConfigFieldControl,
  ConfigTextField,
  IconPicker
} from '@sierralabs/core-ui';
import { useFindEntityDataById } from '@sierralabs/entity-ui';
import {
  SiteComponent,
  SiteComponentConfigProps,
  SiteComponentPlugin,
  SiteComponentProps,
  SiteTaskStatusCardConfigurator,
  useConfigComponent,
  useSiteContext,
  useSubscribeComponentChanges
} from '@sierralabs/site-ui';
import { TaskEntity } from '@sierralabs/workflow-ui';

import AppSnackbar, { AppSnackbarProps } from '../common/AppSnackbar';
import environment from '../environments';
import TaskFileVersionV2ReferenceCard from '../secure-portal/task-v2/cards/TaskFileVersionV2ReferenceCard';

const StyledAlert = styled(Alert)`
  margin-bottom: 8px;
  .MuiAlertTitle-root {
    font-size: 15px;
    font-weight: 600;
  }
`;

export interface TaskFileVersionReferenceCardProps {
  icon?: string;
  title?: string;
  entityId?: string;
  entityTable?: string;
  routeId?: string;
  routeParam?: string;
}

export const TaskFileVersionReferenceCard: React.FC<
  SiteComponentProps<TaskFileVersionReferenceCardProps>
> = props => {
  const component = useSubscribeComponentChanges(
    TaskFileVersionReferenceCardPlugin,
    props
  );
  const { editing, isHover, hoverProps } = useSiteContext();
  const classes = ['task-file-version-reference-card'];
  if (isHover(component)) {
    classes.push('component-hover');
  }
  const params = useParams<Record<string, any>>();
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    severity: AppSnackbarProps['severity'];
    message?: React.ReactNode;
  }>({ open: false, severity: 'success' });
  const { icon, title, entityId, entityTable, routeParam } =
    component.props || {};
  const dataId = routeParam ? params[routeParam] : undefined;
  const { entityData, getEntityDataById } = useFindEntityDataById<TaskEntity>(
    entityId || entityTable || '',
    dataId
  );
  const taskId = entityData?.taskId || 0;

  useEffect(() => {
    getEntityDataById();
  }, [getEntityDataById]);

  return (
    <SiteComponent plugin={TaskFileVersionReferenceCardPlugin} {...props}>
      <TaskFileVersionV2ReferenceCard
        taskId={taskId}
        icon={icon}
        title={title}
        readonly={editing}
        // require taskId if not editing
        disabled={!editing && !taskId}
        className={classes.join(' ')}
        assignButtonLabel='Link Documents'
        cardProps={hoverProps(component) as CardProps}
        onError={error => {
          setSnackbar({
            open: true,
            severity: 'error',
            message: error.message
          });
        }}
        onChange={result => {
          const addCount = result.params?.add?.length || 0;
          const removeCount = result.params?.remove?.length || 0;
          const total = addCount + removeCount;

          const actions: string[] = [];
          addCount > 0 && actions.push('linked');
          removeCount > 0 && actions.push('unlinked');
          const label = actions.length > 0 ? actions.join(' and ') : 'linked';

          setSnackbar({
            open: true,
            severity: 'success',
            message: `Document${total === 1 ? '' : 's'} ${label} successfully.`
          });
        }}
      />

      <AppSnackbar
        open={snackbar.open}
        severity={snackbar.severity}
        onClose={() => setSnackbar(snackbar => ({ ...snackbar, open: false }))}
      >
        {snackbar.message}
      </AppSnackbar>
    </SiteComponent>
  );
};

export const TaskFileVersionReferenceCardConfigurator: React.FC<
  SiteComponentConfigProps<TaskFileVersionReferenceCardProps>
> = props => {
  const { component, update } = useConfigComponent(props.component);
  return (
    <>
      {!environment.fileConfig?.showChangeOrderV2ReferenceCard && (
        <StyledAlert severity='warning'>
          <AlertTitle>Change Order (v2) Not Supported</AlertTitle>
          Change Order (v2) requires additional configuration for this
          environment. Please contact your administrator.
        </StyledAlert>
      )}

      <ConfigFieldControl>
        <IconPicker
          value={component.props?.icon || ''}
          onChange={icon => update('props.icon', icon)}
        />
        <ConfigTextField
          fullWidth
          label='Title'
          value={component.props?.title || ''}
          onChange={event => update('props.title', event.target.value)}
        />
      </ConfigFieldControl>
      <SiteTaskStatusCardConfigurator {...props} />
    </>
  );
};

export const TaskFileVersionReferenceCardPlugin: SiteComponentPlugin<TaskFileVersionReferenceCardProps> =
  {
    name: 'Task File Version Reference Card',
    type: 'task-file-version-reference-card',
    component: TaskFileVersionReferenceCard,
    configurator: TaskFileVersionReferenceCardConfigurator,
    initConfig: component => {
      if (!component.props) component.props = {};
      component.props.icon = 'file_copy';
      component.props.title = 'Document References';
      return component;
    }
  };
